<script setup lang="ts">
import type { Block } from '../../models/index.ts'
import BlockTitle from './fields/BlockTitle.vue'
import BlockSubtitle from './fields/BlockSubtitle.vue'
import BlockDescription from './fields/BlockDescription.vue'
import BlockMedia from './fields/BlockMedia.vue'
import BlockMediaCarousel from './fields/BlockMediaCarousel.vue'
import BlockHeader from './fields/BlockHeader.vue'
import BlockLinks from './fields/BlockLinks.vue'
import BlockLinksMini from './fields/BlockLinksMini.vue'
import { mainBackgroundClasses } from '@/constants'

const props = defineProps<{
  subStyle?: string
  blockData: Block
  isHeaderBlock: boolean // True if this is the first Block on the page
  customClass?: string
}>()

const imageList = props.blockData.media ?? []

const carouselConfig = {
  itemsToShow: 1.5,
  wrapAround: true,
  snapAlign: 'center',
  gap: 50,
  autoplay: 5000,
  transition: 1000,
  breakpoints: {
    640: {
      // sm
      itemsToShow: 1.5,
    },
    768: {
      // md
      itemsToShow: 2.5,
    },
    1280: {
      // xl
      itemsToShow: 3.5,
    },
    1536: {
      // 2xl
      itemsToShow: 4.5,
    },
  },
}

const standAloneBlockMargin = props.isHeaderBlock ? 'py-8' : ''
</script>

<template>
  <div
    class="overflow-hidden"
    :id="blockData.blockId"
    :class="[
      customClass,
      mainBackgroundClasses(blockData.backgroundColour),
      subStyle === 'one' ? '' : 'grid',
    ]"
  >
    <!-- Media as background -->
    <div
      v-if="blockData.mediaPosition === 'background'"
      class="block-media-bg col-start-1 row-start-1 z-0"
    >
      <BlockMedia
        v-if="imageList.length === 1"
        :media="imageList[0]"
        :custom-class="'w-full h-full object-cover'"
      />
    </div>

    <!-- Media and text -->
    <div
      class="block-media-text col-start-1 row-start-1 z-10 relative flex items-end"
      :class="[
        standAloneBlockMargin,
        blockData.mediaPosition === 'right' ? 'flex-row max-w-[1512px] m-auto' : '',
        blockData.mediaPosition === 'left' ? 'flex-row-reverse' : '',
        blockData.mediaPosition === 'bottom' ? 'flex-col' : '',
        blockData.mediaPosition === 'top' ? 'flex-col-reverse' : '',
        subStyle === 'one' ? 'h-full py-16' : '',
        subStyle !== 'one' ? 'my-16' : '',
      ]"
    >
      <!-- Text overlay -->
      <div
        :class="{
          'max-w-[1512px] m-auto': blockData.mediaPosition === 'bottom',
          'mx-12': blockData.mediaPosition !== 'bottom',
        }"
        class="block-text flex flex-col h-full grow"
      >
        <BlockHeader
          class="block-header"
          :text="blockData.header ?? ''"
          :block-id="blockData.blockId"
        />

        <!-- Centre this content vertically -->
        <div class="flex flex-col grow">
          <BlockTitle
            v-if="blockData.title"
            :text="blockData.title ?? ''"
            :size="isHeaderBlock ? 'xl' : 'lg'"
            :align="blockData.mediaPosition === 'background' ? 'center' : 'left'"
            class="block-title"
            :class="blockData.blockId?.includes('case-') ? 'mt-8 font-recoleta' : 'mt-8'"
          />

          <BlockSubtitle
            v-if="blockData.subtitle"
            :text="blockData.subtitle ?? ''"
            :align="blockData.mediaPosition === 'top' ? 'center' : 'left'"
            :class="blockData.blockId?.includes('case-') ? 'font-recoleta' : ''"
          />

          <BlockDescription
            v-if="blockData.description?.length"
            :content="blockData.description"
            :align="blockData.mediaPosition === 'background' ? 'center' : 'left'"
            class="block-desc"
            :custom-class="
              [
                blockData.mediaPosition === 'background'
                  ? 'max-w-4/5 text-2xl self-center'
                  : 'text-2xl',
                subStyle === 'one' ? '' : 'mt-8',
                blockData.blockId?.includes('case-') ? 'font-recoleta' : '',
              ].join(' ')
            "
            :clamp="subStyle === 'one' ? 3 : 0"
          />
          <BlockLinks
            v-if="subStyle !== 'three'"
            :links="blockData.links"
            :justify="
              blockData.mediaPosition === 'top' || blockData.mediaPosition === 'background'
                ? 'center'
                : 'start'
            "
          />
          <BlockLinksMini
            v-if="subStyle === 'three'"
            :links="blockData.links"
            :justify="
              blockData.mediaPosition === 'top' || blockData.mediaPosition === 'background'
                ? 'center'
                : 'start'
            "
          />
        </div>
      </div>

      <!-- Media left, right, or bottom. Bottom uses a carousel if more than one media exists -->
      <div
        v-if="imageList.length && blockData.mediaPosition !== 'background'"
        class="block-media flex self-center place-content-center"
        :class="[
          blockData.mediaPosition === 'right' ? 'mr-10' : '',
          blockData.mediaPosition === 'left' ? 'ml-6' : '',
          blockData.mediaPosition === 'bottom' ? 'mt-6' : '',
          subStyle === 'one' ? 'max-xl:hidden' : '',
          subStyle !== 'one' ? 'min-w-1/3 w-full ' : '',
        ]"
      >
        <!-- Single media -->
        <div v-if="imageList.length === 1" class="flex flex-row justify-center items-center">
          <BlockMedia
            :media="imageList[0]"
            :custom-class="subStyle === 'one' ? 'max-w-48 w-auto' : ''"
            :block-id="blockData.blockId"
          />
        </div>

        <!-- Multiple media goes into a carousel -->
        <BlockMediaCarousel
          v-else
          :media-list="imageList"
          :custom-class="'w-full'"
          :config="carouselConfig"
        />
      </div>
    </div>
  </div>
</template>

<style>
/* =========================
   Existing sections
   ========================= */

#theneed {
  padding-top: 3rem;
  padding-bottom: 3rem;
}
#theneed > div > div:first-of-type {
  width: 100%;
  padding-left: 6rem;
  padding-right: 6rem;
}
#theneed h1 {
  color: #dfeeff;
  max-width: 24em;
  padding-top: 2rem;
}
#theneed p + p {
  margin-top: 2rem;
}
#theneed p {
  line-height: 50px;
  max-width: 42em;
}
#theneed .carousel {
  margin-top: 8rem;
}
#theneed .carousel__track {
  align-items: center;
}
#theneed .carousel__slide {
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  height: max-content;
  background-image: url('@/assets/need-bg1.png');
  aspect-ratio: 131 / 123;
}
#theneed .carousel__slide > div {
  height: 100%;
}
#theneed .carousel__slide img {
  height: 50%;
  width: auto;
}
#theneed .carousel__slide[style*='--bg-index: 2'] {
  background-image: url('@/assets/need-bg2.png');
  aspect-ratio: 17 / 12;
}
#theneed .carousel__slide[style*='--bg-index: 3'] {
  background-image: url('@/assets/need-bg3.png');
  aspect-ratio: 117 / 131;
}
#theneed .carousel__slide[style*='--bg-index: 4'] {
  background-image: url('@/assets/need-bg4.png');
  aspect-ratio: 609 / 332;
}
#theneed .carousel__slide[style*='--bg-index: 5'] {
  background-image: url('@/assets/need-bg5.png');
  aspect-ratio: 189 / 164;
}

#designyourown {
  padding-top: 5rem;
  padding-bottom: 5rem;
  color: #042e2c;
}
#designyourown p {
  font-weight: 300;
  line-height: 50px;
}
#designyourown ul {
  font-size: 22px;
  font-weight: 300;
  line-height: 40px;
  max-width: 24em;
}
#designyourown li {
  padding-top: 1rem;
}
#designyourown .block-links {
  padding-top: 1rem;
}
#designyourown .block-links div {
  padding: 3px 48px;
  background-color: #123a35de;
  font-size: 22px;
}
#designyourown .block-links p {
  font-weight: 200;
}
#designyourown > div,
#tools > div {
  padding-left: 3rem;
  padding-right: 3rem;
}

@media (max-width: 1024px) {
  #designyourown h1 {
    padding-top: 2rem;
  }
  #designyourown > div {
    flex-direction: column-reverse;
  }
}

@media (max-width: 640px) {
  #theneed {
    padding-top: 0;
    padding-bottom: 0;
  }
  #theneed h1 {
    font-size: 2rem;
  }
  #theneed p {
    line-height: 1.5;
    font-size: 1.3rem;
  }
  #theneed > div > div:first-of-type {
    width: 100%;
    padding-left: 3rem;
    padding-right: 3rem;
  }
  #theneed .carousel {
    margin-top: 3rem;
  }
  #designyourown {
    padding-top: 3rem;
    padding-bottom: 3rem;
  }
  #designyourown h1 {
    font-size: 2rem;
  }
  #designyourown p,
  #designyourown ul {
    font-size: 1.3rem;
    font-weight: 200;
    line-height: 1.5;
  }
  #designyourown ul,
  #designyourown li {
    padding-top: 0;
  }
  #designyourown > div > div {
    margin: 0;
    padding-left: 3rem;
    padding-right: 3rem;
    width: 100vw;
  }
  #designyourown > div {
    padding-left: 0;
    padding-right: 0;
    margin: 0;
  }
  #designyourown .block-links a {
    width: 100%;
  }
  #designyourown .block-links a div {
    width: 100%;
  }
  #designyourown .block-links p {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }
}

/* ==========================================================
   Shared Case Styles - Made Partially With AI assistance
   ========================================================== */

/* Header (all cases) */
[id^='case-'][id$='-header'] {
  background-color: white;

  & > div + div > div > div {
    justify-content: center;
  }

  & h1 {
    font-weight: 400;
    font-size: 50px;
    letter-spacing: 1%;
  }

  & p {
    font-weight: 350;
    font-size: 30px;
    line-height: 50px;
    letter-spacing: 1%;
  }
}

/* Need + Qualitative approach (same typography) */
[id^='case-'][id$='-need'] {
  margin-left: auto;
  margin-right: auto;
  width: 70rem;

  & h1 {
    font-weight: 500;
    font-size: 40px;
    line-height: 60px;
    letter-spacing: 1%;
  }

  & span {
    font-weight: 300;
  }

  & p {
    font-weight: 300;
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 1%;
  }

  & ul {
    font-weight: 300;
    font-size: 22px;
  }
}

[id^='case-'][id$='-methodology'] {
  & h1 {
    font-weight: 500;
    font-size: 40px;
    line-height: 60px;
    letter-spacing: 1%;
  }

  & span {
    font-weight: 300;
  }

  & p {
    font-weight: 300;
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 1%;
  }
}

/* Social change (same structure + typography) */
[id^='case-'][id$='-socialchange'] {
  margin-left: 0;
  margin-right: 0;
  margin-top: 5rem;

  & > div {
    margin-left: auto;
    margin-right: auto;
    width: 70rem;
  }

  & h1 {
    font-weight: 500;
    font-size: 40px;
    line-height: 60px;
    letter-spacing: 1%;
  }

  & span {
    font-weight: 300;
  }

  & p {
    font-weight: 300;
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 1%;
  }

  & ul {
    font-weight: 300;
    font-size: 22px;
  }
}

/* Student carousel fix (all cases) */
[id^='case-'][id$='-student'] {
  & .carousel__slide > div:after {
    content: none;
  }
}

/* Methodology details width (all cases that have it) */
[id^='case-'][id$='-methodology-details'] {
  margin-left: auto;
  margin-right: auto;
  width: 70rem;

  & .block-desc {
    margin-top: 1rem;
  }
}

/* Shared Methodology block base */
[id^='case-'][id$='-methodology'] {
  background-color: #e3d7928a;
  margin-right: 0;
  margin-left: 0;
  padding-bottom: 30px;
  justify-items: center;

  & span {
    font-weight: 300;
  }

  & h1 {
    font-weight: 500;
    font-size: 40px;
    line-height: 60px;
    letter-spacing: 1%;
  }

  & p {
    font-weight: 250;
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 1%;
  }

  & > div > div + div {
    margin-left: auto;
    margin-right: auto;
    width: 80rem;
  }

  & .grid-style {
    border-radius: 10px 10px 0 0;
    box-shadow:
      0 6px 0 rgba(0, 0, 0, 0.12),
      0 14px 18px -10px rgba(0, 0, 0, 0.25);

    & h1 {
      font:
        normal 500 26px/40px 'Recoleta Alt',
        serif;
      letter-spacing: 0.01em;
      text-align: center;
    }

    & p {
      font:
        250 17px/1.4 'Alexandria',
        sans-serif;
      letter-spacing: 0.01em;
      text-align: center;
      margin-left: 7px;
      margin-right: 7px;
      margin-bottom: 5px;
    }

    & > .col-start-1 {
      align-items: flex-start;
      margin-top: 0;
      margin-bottom: 0;
    }

    & .flex-col.justify-end {
      justify-content: flex-start;
    }
  }
}

#case-kjeller-methodology {
  & .grid-style {
    box-shadow: none;
  }
}

/* Shared Experience block base */
[id^='case-'][id$='-experience'] {
  background-color: #042e2c;
  margin-right: 0;
  margin-left: 0;
  margin-top: 5rem;
  justify-items: center;

  & > div {
    background-color: #042e2c;
    margin-left: auto;
    margin-right: auto;
    width: 70rem;

    & > div {
      background-color: #042e2c;
    }
  }

  & > div + div {
    background-color: #042e2c;
    margin-left: auto;
    margin-right: auto;
    width: 70rem;
  }

  & h1 {
    font-weight: 500;
    font-size: 35px;
    line-height: 60px;
    letter-spacing: 1%;
  }

  & span {
    font-weight: 300;
  }

  & p {
    font-weight: 300;
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 1%;
  }
}

/* Shared Teacher block base */
[id^='case-'][id$='-teacher'] {
  margin-right: 0;
  margin-left: 0;
  margin-top: 5rem;
  justify-items: center;

  & > div {
    margin-left: auto;
    margin-right: auto;

    width: auto;
    max-width: 90rem;
    padding-left: 3rem;
    padding-right: 3rem;

    & > div {
      width: 50%;

      & > div > div {
        font-size: 22px;
      }
    }

    & > div + div > div {
      margin-left: 3rem;
    }
  }

  & h1 {
    font-family: 'Recoleta Alt', serif;
    font-weight: 500;
    font-size: 35px;
    line-height: 55px;
    letter-spacing: 1%;
  }

  & p {
    font-family: 'Recoleta Alt', serif;
    font-weight: 300;
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 1%;
  }

  & span {
    font-weight: 300;
    font-size: 16px;
  }
}

/* =========================
   Per-case overrides (only what differs)
   ========================= */

#case-oslo-socialchange {
  & > div {
    width: 70rem;
  }
}

/* NOTE: These are fixed heights; keep if you truly want fixed boxes.
   If you want content-driven heights, remove these entirely (or switch to min-height). */
[id^='case-'][id$='-methodology'] {
  & .grid:nth-of-type(2) > *:nth-child(odd) .grid-style {
    height: auto;
    min-height: 340px;
  }
  & .grid:nth-of-type(2) > *:nth-child(even) .grid-style {
    height: auto;
    min-height: 300px;
  }
}

#case-kjeller-methodology {
  & .grid:nth-of-type(2) > *:nth-child(odd) .grid-style {
    height: auto;
    min-height: 0;
  }
  & .grid:nth-of-type(2) > *:nth-child(even) .grid-style {
    height: auto;
    min-height: 0;
  }
}

.grid-style {
  padding: 10px;
  border-radius: 10px;
}
.grid-style img {
  margin-top: 10px;
  border-radius: 20px;
}
.grid-style div {
  margin: 0;
  align-items: center;
}
.grid-style h1 {
  font-size: 1.5rem;
  margin: 1rem 0;
}
.grid-style .strapi-block {
  font-size: 1rem;
}
.grid-style-one h1 {
  color: white;
}
.grid-style-two h1 {
  color: black;
}
.grid-style-three h1 {
  font-family: var(--font-alexandria);
  color: black;
}
.grid-style-three h3 {
  font-family: var(--font-alexandria);
  font-weight: 200;
  line-height: 2rem;
}
.grid-style-four h1 {
  color: black;
}
.grid-style .grid-style-one .strapi-block {
  color: white;
}
.grid-style .grid-style-two .strapi-block {
  color: white;
}
.grid-style .grid-style-three .strapi-block {
  color: black;
}
.grid-style .grid-style-four .strapi-block {
  color: black;
}
.grid-style-three img {
  border-radius: 20px;
}
.grid-style .block-links {
  align-items: center;
  justify-items: center;
}

/* Use max-width + padding instead of fixed widths */
[id^='case-'][id$='-need'],
[id^='case-'][id$='-qualitative-approach'],
[id^='case-'][id$='-methodology-details'] {
  width: auto; /* override fixed 70rem */
  max-width: 70rem;
  padding-left: 3rem;
  padding-right: 3rem;
}

/* Social change: override inner fixed width */
[id^='case-'][id$='-socialchange'] > div {
  width: auto; /* override fixed 70rem */
  max-width: 70rem;
  padding-left: 3rem;
  padding-right: 3rem;
}

/* Experience: override inner fixed width */
[id^='case-'][id$='-experience'] > div,
[id^='case-'][id$='-experience'] > div + div {
  width: auto; /* override fixed 70rem */
  max-width: 70rem;
  padding-left: 3rem;
  padding-right: 3rem;
}

/* Methodology grid wrapper: override fixed 80rem */
[id^='case-'][id$='-methodology'] > div > div + div {
  width: auto; /* override fixed 80rem */
  max-width: 80rem;
  padding-left: 3rem;
  padding-right: 3rem;
}

/* Laptop/tablet adjustments */
@media (max-width: 1300px) {
  [id^='case-'][id$='-need'],
  [id^='case-'][id$='-qualitative-approach'],
  [id^='case-'][id$='-methodology-details'],
  [id^='case-'][id$='-socialchange'] > div,
  [id^='case-'][id$='-experience'] > div,
  [id^='case-'][id$='-experience'] > div + div,
  [id^='case-'][id$='-methodology'] > div > div + div {
    padding-left: 2rem;
    padding-right: 2rem;
  }

  [id^='case-'][id$='-methodology'] {
    & .grid:nth-of-type(2) {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }
    & .grid:nth-of-type(2) > *:nth-child(odd) .grid-style {
      height: auto;
      min-height: 0;
    }
    & .grid:nth-of-type(2) > *:nth-child(even) .grid-style {
      height: auto;
      min-height: 0;
    }
    & .grid-style {
      width: 300px;
      & p {
        margin-top: 0;
        margin-bottom: 0;
      }
    }
  }

  /* Teacher two-column -> stacked */
  [id^='case-'][id$='-teacher'] > div {
    padding-left: 8rem;
    padding-right: 4rem;
    flex-direction: column;
  }
  [id^='case-'][id$='-teacher'] > div > div {
    width: 100%;
  }
  [id^='case-'][id$='-teacher'] > div > div + div > div {
    margin-left: -2rem;
    margin-top: 2rem;
  }
}

/* Mobile typography + spacing */
@media (max-width: 640px) {
  [id^='case-'][id$='-need'],
  [id^='case-'][id$='-qualitative-approach'],
  [id^='case-'][id$='-methodology-details'],
  [id^='case-'][id$='-socialchange'] > div,
  [id^='case-'][id$='-methodology'] > div > div + div {
    padding-left: 1.25rem;
  }

  [id^='case-'][id$='-experience'] {
    overflow-x: clip;
  }

  [id^='case-'][id$='-teacher'] h1 {
    font-size: 30px;
  }

  [id^='case-'][id$='-experience'] > div,
  [id^='case-'][id$='-experience'] > div + div {
    width: 100%;
    max-width: 70rem;
    margin-left: auto;
    margin-right: auto;
    padding-left: 1.25rem;
    padding-right: 1.25rem;
    box-sizing: border-box;
  }

  /* Header scale down */
  [id^='case-'][id$='-header'] h1 {
    font-size: 2.2rem;
    line-height: 1.2;
  }
  [id^='case-'][id$='-header'] p {
    font-size: 1.3rem;
    line-height: 1.4;
  }

  /* Section headings scale down */
  [id^='case-'][id$='-need'] h1,
  [id^='case-'][id$='-qualitative-approach'] h1,
  [id^='case-'][id$='-socialchange'] h1,
  [id^='case-'][id$='-experience'] h1,
  [id^='case-'][id$='-methodology'] h1 {
    font-size: 2rem;
    line-height: 1.25;
  }

  /* Paragraphs scale down */
  [id^='case-'][id$='-need'] p,
  [id^='case-'][id$='-qualitative-approach'] p,
  [id^='case-'][id$='-socialchange'] p,
  [id^='case-'][id$='-experience'] p,
  [id^='case-'][id$='-methodology'] p {
    font-size: 1.1rem;
    line-height: 1.5;
  }

  [id^='case-'][id$='-methodology'] {
    & .grid-style {
      width: auto;
    }
  }
}
</style>
